import { ArrowDown, ArrowLeft, Maximize2, Menu, Plus, Search } from "lucide-react";
import { StudioDesktopWindowControls } from "@/studio/shell/StudioDesktopWindowControls";
import { StudioAgentAvatar } from "@/studio/components/StudioAgentAvatar";
import { StudioPaneSkeletonBootShell } from "@/studio/components/chrome/studio-pane-skeleton-boot";
import { PaneVariantBody, ShellMobileNavStub } from "@/studio/components/chrome/studio-pane-skeleton-parts";
import type { ReactNode } from "react";
import "@/studio/components/studio-shell-paint.css";
import "@/studio/components/studio-workspace-head.css";
import "./studio-boot-frame.css";

/* Chat list placeholders under the agent row (default landing: the agent
   chat on top, a few people below). */
const SKELETON_ROWS = [0.46, 0.62, 0.38];

/** Default landing tab (DMs). The reveal script swaps in the saved tabs. */
const DEFAULT_TAB = "DMs";

const SVG_ATTRS =
  'xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"';

/*
 * Tab strip as static HTML: the reveal script (studio-boot-frame-script)
 * rebuilds it from the saved tabs before React hydrates, so React must not
 * own these nodes (same reason as the old greeting pill). Glyphs are the
 * live tab's chat square (13px) and lucide X.
 */
const TABS_HTML =
  `<span class="ysbf-tabs"><span class="ysbf-tab is-active">` +
  `<svg class="ysbf-tab-icon" ${SVG_ATTRS}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>` +
  `<span class="ysbf-tab-label">${DEFAULT_TAB}</span>` +
  `<span class="ysbf-tab-x"><svg ${SVG_ATTRS}><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></span>` +
  `</span></span>`;

/** Chat side panel tabs (StudioMessagesPane peer sidebar), Files first. */
const PEER_TABS = ["Files", "Requests", "Computer \u00b7 Soon", "Settings"];

/*
 * Right side panel of the open chat (.studio-dm-peer-sidebar, open by
 * default): tab pill, search, folder crumbs, 2-column file tiles. Hidden when
 * the user closed it (html[data-ys-boot-peer="0"], set by the head script).
 */
function YsbfPeerPanel() {
  return (
    <aside className="ysbf-peer">
      <div className="ysbf-peer-head">
        <span className="ysbf-peer-tabs">
          {PEER_TABS.map((label, index) => (
            <span className={`ysbf-peer-tab${index === 0 ? " is-active" : ""}`} key={label}>
              {label}
            </span>
          ))}
        </span>
      </div>
      <span className="ysbf-search ysbf-peer-search">
        <Search />
      </span>
      <div className="ysbf-peer-crumbs">
        <span className="ysbf-circle is-36"><ArrowLeft /></span>
        <span className="ysbf-peer-track">
          <span className="ysbf-peer-chip" />
          <span className="ysbf-peer-chip is-current" />
        </span>
        <span className="ysbf-circle is-36"><Plus /></span>
      </div>
      <div className="ysbf-peer-grid">
        {Array.from({ length: 8 }, (_, index) => (
          <span className="ysbf-peer-tile" key={index} />
        ))}
      </div>
    </aside>
  );
}

function YsbfAuthGreet({ greeting }: { greeting?: { title: string; quoteText: string; quoteName: string } }) {
  return (
    <header className="ysbf-auth-greet" aria-hidden="true">
      <div className="ysbf-auth-greet-copy">
        {greeting ? (
          <>
            <strong>{greeting.title}</strong>
          </>
        ) : (
          <span className="ysbf-greet" suppressHydrationWarning />
        )}
      </div>
    </header>
  );
}

/*
 * Desktop first paint, laid out like the loaded shell (StudioShellView):
 * 36px head (tab strip left; clock, full-screen and menu circles right),
 * then the card: chat list rail and the open agent chat (head, bubbles,
 * composer). Numbers in studio-boot-frame.css / studio-pane-skeleton.css.
 */
function StudioBootDesktopSkeleton({ main }: { main?: ReactNode }) {
  return (
    <div className="ysbf-stage studio-boot-skel-desktop" aria-hidden="true">
      <header className="ysbf-head">
        <span
          className="ysbf-pill ysbf-nav"
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: TABS_HTML }}
        />
        <span className="ysbf-pill ysbf-tools">
          <span className="ysbf-time" />
          <span className="ysbf-hbtn"><Maximize2 /></span>
          <span className="ysbf-hbtn"><Menu /></span>
        </span>
      </header>
      <div className="ysbf-card">
        <div className="ysbf-body">
          <aside className="ysbf-rail">
            <div className="ysbf-rail-card">
              <div className="ysbf-rail-chrome">
                <span className="ysbf-search">
                  <Search />
                  <span className="ysbf-search-text">Search people, chats &amp; messages</span>
                </span>
                <span className="ysbf-filter">
                  <span>All</span>
                  <ArrowDown />
                </span>
                <span className="ysbf-circle is-36"><Plus /></span>
              </div>
              <div className="ysbf-rows">
                <span className="ysbf-row is-agent">
                  <span className="ysbf-row-avatar is-agent">
                    <StudioAgentAvatar size={48} state="idle" staticFace label="Studio" />
                  </span>
                  <span className="ysbf-row-copy">
                    <span className="ysbf-row-line is-name">
                      <span style={{ width: "34%" }} />
                      <span className="ysbf-row-time" />
                    </span>
                    <span className="ysbf-row-line is-preview">
                      <span style={{ width: "86%" }} />
                    </span>
                  </span>
                </span>
                {SKELETON_ROWS.map((width, index) => (
                  <span className="ysbf-row" key={index}>
                    <span className="ysbf-row-avatar" />
                    <span className="ysbf-row-copy">
                      <span className="ysbf-row-line is-name">
                        <span style={{ width: `${Math.round(width * 100)}%` }} />
                        <span className="ysbf-row-time" />
                      </span>
                      <span className="ysbf-row-line is-preview">
                        <span style={{ width: `${Math.round((width + 0.3) * 100)}%` }} />
                      </span>
                    </span>
                  </span>
                ))}
              </div>
            </div>
          </aside>
          <div className="ysbf-main">
            <section className="ysbf-chat">
              {main === undefined ? (
                <div className="studio-pane-skeleton-embedded ysbf-thread">
                  <PaneVariantBody variant="messages-thread" />
                </div>
              ) : (
                main
              )}
            </section>
          </div>
          {main === undefined ? <YsbfPeerPanel /> : null}
        </div>
      </div>
    </div>
  );
}

export function StudioSkeletonCard({
  main,
  auth = false,
  greeting,
}: {
  main?: ReactNode;
  auth?: boolean;
  greeting?: { title: string; quoteText: string; quoteName: string };
} = {}) {
  if (auth) {
    return (
      <div className="ysbf-stage is-auth">
        <YsbfAuthGreet greeting={greeting} />
        <div className="studio-desktop-auth-controls">
          <StudioDesktopWindowControls />
        </div>
        <div className="ysbf-auth-main">{main}</div>
        <ShellMobileNavStub selected={false} />
      </div>
    );
  }

  return (
    <>
      <StudioBootDesktopSkeleton main={main} />
      <div className="studio-boot-skel-mobile">
        <StudioPaneSkeletonBootShell
          variant="messages-thread"
          tab={DEFAULT_TAB}
          main={main === undefined ? undefined : <section className="ysbf-chat">{main}</section>}
        />
      </div>
    </>
  );
}
